import { useAuthStore } from '../stores/authStore';

const API_URL =
  (import.meta.env?.VITE_API_URL as string | undefined) ||
  (import.meta.env?.DEV ? '/api' : 'https://api.summio.org');

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'super_admin' | 'content_manager' | 'analyst' | 'support';
}

export interface LoginResponse {
  token: string;
  user: AdminUser;
}

class ApiService {
  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = useAuthStore.getState().token;
    const headers = new Headers(options.headers);
    headers.set('Content-Type', headers.get('Content-Type') ?? 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      useAuthStore.getState().logout();
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Invalid credentials' }));
      throw new Error(error.error || 'Invalid credentials');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    try {
      await this.request('/admin/auth/logout', { method: 'POST' });
    } finally {
      useAuthStore.getState().logout();
    }
  }
}

export const apiService = new ApiService();
